import { Link } from 'react-router-dom';
import { ArrowRight, BarChart2, Zap, Cpu } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function SolutionsSecurityForAi() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Solutions · Security for AI</p>
          <h1 className="text-2xl md:text-[1.85rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl">
            Protect models and traffic in production
          </h1>
          <p className="mt-4 text-[#5c5c5c] max-w-2xl leading-relaxed">
            This line is about runtime and artefact protection: inspecting what you deploy and guarding how it is used—via
            ModelFort and GuardFort.
          </p>
        </RfPageFrame>
      </div>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="grid md:grid-cols-2 gap-6">
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <h2 className="text-lg font-semibold text-[#2c2c2c] mb-2">ModelFort</h2>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Static and dynamic analysis of models and related artefacts to catch integrity and safety issues before
                they become incidents.
              </p>
              <Link to="/modelfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                Open ModelFort <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <h2 className="text-lg font-semibold text-[#2c2c2c] mb-2">GuardFort</h2>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                An AI firewall posture for real traffic—monitoring, blocking, and alerting on abusive or unsafe interactions.
              </p>
              <Link to="/guardfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                Open GuardFort <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>

          <p className="mt-10 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/solutions/ai-security" className="text-[#005a9c] font-medium hover:text-[#004a82]">
              ← Solutions overview
            </Link>
          </p>
        </RfPageFrame>
      </section>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-[#2c2c2c]">Human Trust</h2>
              <p className="mt-2 text-[0.9375rem] text-[#5c5c5c] max-w-2xl">
                Packaged solutions that make human judgment reliable in the AI era — from risk assessment to managed simulations to copilot readiness.
              </p>
            </div>
            <Link
              to="/services/human-trust"
              className="text-[0.9375rem] font-medium text-[#005a9c] inline-flex items-center gap-1 shrink-0"
            >
              Human Trust services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <div className="w-10 h-10 bg-[#E1F5EE] rounded-lg flex items-center justify-center mb-4">
                <BarChart2 className="w-5 h-5 text-[#0F6E56]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">TrustScore</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Human risk assessment for the AI era. Quantified score across five dimensions — AI threat recognition, verification behaviour, reporting instincts, policy compliance, and shadow AI usage. Board-ready report.
              </p>
              <Link to="/solutions/trustscore" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                TrustScore <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <div className="w-10 h-10 bg-[#FAECE7] rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-[#993C1D]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">DeepStrike</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Managed AI attack simulation campaigns. Quarterly multi-vector exercises combining prompt injection emails, deepfake vishing, and synthetic identity probes. Fully managed with white-labelled reporting.
              </p>
              <Link to="/solutions/deepstrike" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                DeepStrike <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <div className="w-10 h-10 bg-[#E6F1FB] rounded-lg flex items-center justify-center mb-4">
                <Cpu className="w-5 h-5 text-[#185FA5]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">AgentReady</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Human readiness for enterprise AI agent rollouts — Microsoft 365 Copilot, Google Workspace AI, Salesforce Einstein, ServiceNow AI, and any other platform. Pre-deployment training, policies, and TrustScore baseline. Post-deployment: ongoing simulation and measurement.
              </p>
              <Link to="/solutions/copilotready" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                AgentReady <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>
        </RfPageFrame>
      </section>
    </>
  );
}
